import { jsxs, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { EGDSHeadingTags, defaultMenuHeadingTag } from '../types/index.esm.js';
import styles from '../../styles/uitk-egds-menu.css';
import { EGDSContext } from '../../utils/context-provider/index.esm.js';
import { classNames } from '../../utils/classnames/index.esm.js';
import { validatePropType } from '../../utils/validate-prop-types/index.esm.js';
const getClassNameList = classNames.bind(styles);
var PositionType;
(function (PositionType) {
  PositionType["RIGHT"] = "right";
  PositionType["LEFT"] = "left";
})(PositionType || (PositionType = {}));
class EGDSMenuContainer extends React.Component {
  static displayName = 'EGDSMenuContainer';
  static defaultProps = {
    position: PositionType.RIGHT
  };
  static contextType = EGDSContext;
  intersectionObserver;
  // Private resizeObserver: any;
  // private resizeObserverIsBound: boolean;
  // private resizeWidth: number;
  constructor(props) {
    super(props);
    this.state = {
      animationHasEnded: false,
      offset: {
        x: 0,
        y: 0
      }
    };
    this.autoPositionCallback = this.autoPositionCallback.bind(this);
    this.adjustAlignment = this.adjustAlignment.bind(this);
    this.resetMenuContainer = this.resetMenuContainer.bind(this);
    this.onAnimationEndCallback = this.onAnimationEndCallback.bind(this);
    this.animationEndedStateCallback = this.animationEndedStateCallback.bind(this);
    this.unobserveIntersectionObserver = this.unobserveIntersectionObserver.bind(this);
    this.onMenuContainerClose = this.onMenuContainerClose.bind(this);
    // This.resizeObserverHandler = this.resizeObserverHandler.bind(this);
    // this.resizeObserverIsBound = false;
    // this.resizeWidth = 0;
  }
  componentDidMount() {
    const {
      autoPosition,
      isOpen
    } = this.props;
    isOpen && autoPosition && this.autoPositionObserver(); // Setting up auto position observer here for instances where the menu container is added/removed from DOM as opposed to visibility toggled.
  }
  componentDidUpdate(prevProps) {
    const {
      autoPosition,
      isOpen
    } = this.props;
    if (isOpen !== prevProps.isOpen) {
      if (isOpen) {
        autoPosition && this.autoPositionObserver(); // Setting up auto position observer here for instances where the menu container is added to the DOM, but visibility is toggled.
      } else {
        this.onMenuContainerClose();
      }
    }
  }
  componentWillUnmount() {
    this.onMenuContainerClose();
  }
  onMenuContainerClose() {
    this.resetMenuContainer();
    this.unobserveIntersectionObserver();
  }
  onAnimationEndCallback(e) {
    const {
      animationHasEnded
    } = this.state;
    // There are multiple animationEnd callbacks due to there being 2-3 animations applied, this logic ensures animationEndedStateCallback() is only called once.
    !animationHasEnded && e.animationName.match(/menu-grow-/) && this.setState({
      animationHasEnded: true
    }, this.animationEndedStateCallback);
  }
  // This callback may appear to be doing something similar to adjustAlignment(), but it's actually only required for Safari desktop.
  // Safari desktop doesn't currently support the use of css variables inside keyframe animations. Therefore the updates to --menu-grow-x and --menu-grow-y have no impact in safari desktop.
  // In order to get the auto positioning working in safari desktop, we need to use transform: translate and set css variables for x and y to be used in the translate.
  // There's also a corresponding CSS "hack" to target safari desktop only ln 59 in uitk-menu.scss
  // The reason all of this is done once the animation is complete, is because if we do this during the animation, it causes some jankiness.
  animationEndedStateCallback() {
    const {
      containerRef
    } = this.props;
    // eslint-disable-next-line no-negated-condition
    const viewport = typeof document !== 'undefined' ? document.documentElement : null;
    const windowInnerHeight = window.innerHeight;
    if (containerRef?.current && viewport) {
      const container = containerRef.current;
      const containerBoundingClientRect = container.getBoundingClientRect();
      const viewportBoundingClientRect = viewport.getBoundingClientRect();
      let offsetX = 0;
      let offsetY = 0;
      if (containerBoundingClientRect.right > viewportBoundingClientRect.right) {
        offsetX = -Math.abs(Math.ceil(containerBoundingClientRect.right) - Math.ceil(viewportBoundingClientRect.right));
      } else if (containerBoundingClientRect.left < viewportBoundingClientRect.left) {
        offsetX = Math.abs(containerBoundingClientRect.left);
      }
      if (containerBoundingClientRect.bottom > windowInnerHeight) {
        offsetY = -Math.abs(Math.ceil(containerBoundingClientRect.bottom) - Math.ceil(windowInnerHeight));
      }
      if (offsetX || offsetY) {
        container.style.setProperty('--menu-offset-translate-x', `${offsetX}px`);
        container.style.setProperty('--menu-offset-translate-y', `${offsetY}px`);
      }
    }
    document.addEventListener('scroll', this.unobserveIntersectionObserver);
    window.addEventListener('resize', this.unobserveIntersectionObserver);
  }
  unobserveIntersectionObserver() {
    const {
      autoPosition,
      containerRef
    } = this.props;
    if (autoPosition && containerRef.current) {
      this.intersectionObserver && this.intersectionObserver.unobserve(containerRef.current);
      // This.resizeObserver &&
      //   this.resizeObserver.unobserve(this.props.containerRef!.current);
    }
  }
  autoPositionObserver() {
    const {
      containerRef,
      intersectionObserverRoot
    } = this.props;
    if (!(window && window.IntersectionObserver)) {
      return false;
    }
    const thresholdSet = [];
    for (let i = 0.0; i <= 1.0; i += 0.01) {
      thresholdSet.push(i);
    }
    const defaultIntersectionObserverOptions = {
      threshold: thresholdSet
    };
    const intersectionObserverOptions = intersectionObserverRoot ? Object.assign(defaultIntersectionObserverOptions, {
      root: document.querySelector(intersectionObserverRoot)
    }) : defaultIntersectionObserverOptions;
    this.intersectionObserver = new IntersectionObserver(this.autoPositionCallback, intersectionObserverOptions);
    containerRef.current && this.intersectionObserver.observe(containerRef.current);
  }
  autoPositionCallback(entries) {
    entries.forEach(entry => {
      const formattedRatio = Math.round(parseFloat(entry.intersectionRatio.toString()) * 100) / 100;
      if (formattedRatio <= 0.99 && entry.intersectionRatio > 0) {
        this.adjustAlignment(entry);
      }
    });
  }
  adjustAlignment(entry) {
    // Set offset state to be used as measure for translate: transformX
    // !this.resizeObserverIsBound &&
    //   typeof ResizeObserver !== 'undefined' &&
    //   this.resizeObserverHandler(); // Instantiate a resize observer in case user resizes window or changes orientation
    const {
      context
    } = this.context;
    // This fixes a regression caused by https://github.expedia.biz/egds/uitk-react/pull/6320
    if (context === 'egds-date-picker-context' && Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom)) {
      const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
      this.setState(prevState => ({
        offset: {
          x: prevState.offset.x,
          y: -Math.abs(prevState.offset.y - currentOffsetY)
        }
      }));
      return;
    }
    if (Math.ceil(entry.boundingClientRect.right) > Math.ceil(entry.intersectionRect.right)) {
      const currentOffsetX = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
      this.setState(prevState => ({
        offset: {
          x: -Math.abs(prevState.offset.x - currentOffsetX),
          y: prevState.offset.y
        }
      }));
    } else if (Math.ceil(entry.boundingClientRect.left) < Math.ceil(entry.intersectionRect.left)) {
      const currentOffsetX = entry.boundingClientRect.left - entry.intersectionRect.left;
      this.setState(prevState => ({
        offset: {
          x: prevState.offset.x - currentOffsetX,
          y: prevState.offset.y
        }
      }));
    }
    const {
      adjustmentType
    } = this.state;
    if (Math.ceil(entry.boundingClientRect.bottom) > window.innerHeight) {
      // Checks if the menu container is taller than the viewport
      const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - window.innerHeight;
      this.setState(prevState => ({
        offset: {
          x: prevState.offset.x,
          y: 0
        },
        adjustedHeight: entry.boundingClientRect.height - Math.abs(prevState.offset.y - currentOffsetY),
        adjustmentType: 'window'
      }));
    }
    if (Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom)) {
      // Checks if the menu container is taller than the defined root
      const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
      if (entry.boundingClientRect.height > entry.intersectionRect.height) {
        // Checks if the menu container is taller than the defined root
        this.setState(prevState => ({
          offset: {
            x: prevState.offset.x,
            y: 0
          },
          adjustedHeight: entry.boundingClientRect.height - Math.abs(prevState.offset.y - currentOffsetY),
          adjustmentType: 'container'
        }));
      } else {
        // If the menu container is not taller than the defined root, shifts the menu container up
        this.setState(prevState => ({
          offset: {
            x: prevState.offset.x,
            y: -Math.abs(prevState.offset.y - currentOffsetY)
          }
        }));
      }
    } else if (adjustmentType === 'window') {
      // Remove window adjustment if no longer needed
      this.setState(() => ({
        adjustedHeight: undefined,
        adjustmentType: undefined
      }));
    }
  }
  resetMenuContainer() {
    this.setState({
      offset: {
        x: 0,
        y: 0
      },
      animationHasEnded: false
    });
    const {
      containerRef
    } = this.props;
    containerRef?.current?.style.removeProperty('--menu-offset-translate-x');
    containerRef?.current?.style.removeProperty('--menu-offset-translate-y');
    document.removeEventListener('scroll', this.unobserveIntersectionObserver);
    window.removeEventListener('resize', this.unobserveIntersectionObserver);
    // This.resizeObserver &&
    //   this.resizeObserver.unobserve(document.documentElement);
    // this.resizeObserverIsBound = false;
  }
  // Decided not to use in production as of 12/2019 due to ResizeObserver being "Editor's Draft". If the specification becomes CR / REC then this functionality can be re-introduced
  // private resizeObserverHandler() {
  //   this.resizeObserverIsBound = true;
  //   // @ts-ignore
  //   this.resizeObserver = new ResizeObserver(entries => {
  //     for (const entry of entries) {
  //       const resizeCurrentWidth = entry.contentRect.width;
  //       if (this.resizeWidth > 0 && resizeCurrentWidth > this.resizeWidth) {
  //         // If resize width is increasing - re-adjust offset
  //         if (this.props.position === PositionType.RIGHT) {
  //           this.props.containerRef!.current &&
  //             this.setState(
  //               state => ({
  //                 offset:
  //                   state.offset -
  //                   Math.ceil(
  //                     this.props.containerRef!.current!.getBoundingClientRect()
  //                       .left
  //                   )
  //               }),
  //               this.handleMenuContainerOffset
  //             );
  //         } else {
  //           this.props.containerRef!.current &&
  //             this.setState(
  //               state => ({
  //                 offset:
  //                   state.offset +
  //                   (Math.ceil(entry.contentRect.right) -
  //                     Math.ceil(
  //                       this.props.containerRef!.current!.getBoundingClientRect()
  //                         .right
  //                     ))
  //               }),
  //               this.handleMenuContainerOffset
  //             );
  //         }
  //       }
  //       this.resizeWidth = resizeCurrentWidth;
  //     }
  //   });
  //   this.resizeObserver.observe(document.documentElement);
  // }
  render() {
    const {
      autoPosition,
      containerClasses,
      containerRef,
      children,
      position,
      className,
      isOpen,
      maxHeight,
      width,
      title,
      allyHeadingLevel,
      intersectionObserverRoot,
      showOverTrigger
    } = this.props;
    const {
      offset: {
        x,
        y
      },
      adjustedHeight
    } = this.state;
    const {
      context
    } = this.context;
    const wrapperClassNames = getClassNameList(className, containerClasses, {
      [`uitk-menu-pos-${position}`]: validatePropType(position, PositionType),
      'uitk-menu-container-autoposition': !!autoPosition,
      'uitk-menu-container-text-nowrap': !width,
      'uitk-menu-container-has-intersection-root-el': !!intersectionObserverRoot,
      'uitk-menu-container-over-trigger': !!showOverTrigger
    });
    const divStyle = {};
    if (context === 'uitk-date-picker-context') {
      divStyle.maxHeight = `${maxHeight}px`;
      divStyle.overflow = 'auto';
    } else {
      let containerHeight = adjustedHeight || maxHeight;
      if (adjustedHeight && maxHeight) {
        containerHeight = Math.min(adjustedHeight, maxHeight);
      }
      if (containerHeight) {
        divStyle.maxHeight = `${containerHeight}px`;
        divStyle.overflow = 'auto';
      }
    }
    if (width) {
      divStyle.width = `${width}px`;
    }
    if (x || y) {
      divStyle['--menu-grow-x'] = `${x}px`;
      divStyle['--menu-grow-y'] = `${y}px`;
    }
    const HeadingLevel = allyHeadingLevel ? EGDSHeadingTags.get(allyHeadingLevel) : EGDSHeadingTags.get(defaultMenuHeadingTag);
    return jsxs("div", {
      className: wrapperClassNames,
      ref: containerRef,
      "aria-hidden": !isOpen,
      style: divStyle,
      onAnimationEnd: this.onAnimationEndCallback,
      children: [title && jsx(HeadingLevel, {
        className: getClassNameList('uitk-menu-title'),
        children: title
      }), children]
    });
  }
}
export { EGDSMenuContainer, PositionType };